import { FormEvent, useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { Driver, ListResponse } from "../api/types";
import { useSSEQuery } from "../hooks/useSSEQuery";

const ROLE_OPTIONS = [
  { value: "driver", label: "Driver", prefix: "Any non ADM/OPS/DSP/MNT ID" },
  { value: "dispatcher", label: "Dispatcher", prefix: "DSP" },
  { value: "operations_supervisor", label: "Operations Supervisor", prefix: "OPS" },
  { value: "maintenance", label: "Maintenance", prefix: "MNT" },
  { value: "admin", label: "Admin", prefix: "ADM" },
] as const;

const STATUS_OPTIONS = ["active", "inactive", "on_leave", "suspended", "terminated"] as const;

const DRIVER_STREAM_FALLBACK_MS = 5000;

// Vite resolves import.meta.env at build time; keep the same prefix the
// API client uses so the SSE endpoint hits the same host.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) return value.data;
  return [];
}

function inferRole(employeeId: string): string {
  const eid = employeeId.trim().toUpperCase();
  if (eid.startsWith("ADM")) return "admin";
  if (eid.startsWith("OPS")) return "operations_supervisor";
  if (eid.startsWith("DSP")) return "dispatcher";
  if (eid.startsWith("MNT")) return "maintenance";
  return "driver";
}

function roleLabel(role: string): string {
  const opt = ROLE_OPTIONS.find((r) => r.value === role);
  if (role === "operations_supervisor") return "Supervisor";
  return opt?.label ?? role;
}

function roleTone(role: string): "ok" | "info" | "muted" | "warn" {
  if (role === "admin") return "ok";
  if (role === "operations_supervisor") return "info";
  if (role === "maintenance") return "warn";
  return "muted"; // dispatcher, driver
}

function initials(first: string, last: string): string {
  const s = `${(first?.[0] ?? "")}${(last?.[0] ?? "")}`.toUpperCase();
  return s || "U";
}

/** "16:04 today" / "Yesterday" / "Jul 28" / "never" — all derived from the
 *  real last_login_at column (set by the backend on successful auth). */
function fmtLastLogin(iso?: string): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  if (d.toDateString() === now.toDateString()) return `${time} today`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function UsersPage() {
  const { session } = useAuth();
  const isAdmin = session?.role?.toLowerCase() === "admin";

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  // Bus drivers use the tablet app, not this console — default to the
  // accounts that actually sign into the dashboard, but keep every driver
  // reachable (edit/delete/reset-PIN) one click away rather than removing
  // that capability from this, the only account-management screen.
  const [showAllAccounts, setShowAllAccounts] = useState(false);

  const [role, setRole] = useState<(typeof ROLE_OPTIONS)[number]["value"]>("driver");
  const [employeeId, setEmployeeId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pin, setPIN] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]>("active");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<(typeof ROLE_OPTIONS)[number]["value"]>("driver");
  const [editEmployeeId, setEditEmployeeId] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editStatus, setEditStatus] = useState<(typeof STATUS_OPTIONS)[number]>("active");
  const [editPending, setEditPending] = useState(false);
  const [resetPIN, setResetPIN] = useState("");
  const [resetPending, setResetPending] = useState(false);
  const [deletePendingId, setDeletePendingId] = useState<string | null>(null);

  const tokenGetter = () => session?.accessToken ?? null;
  const driversStream = useSSEQuery<Driver[]>(
    `${API_BASE}/drivers/stream`,
    tokenGetter,
    () => api.listDrivers().then((res) => unwrap(res)),
    [session?.accessToken],
    { fallbackIntervalMs: DRIVER_STREAM_FALLBACK_MS }
  );

  async function loadDrivers() {
    setLoading(true);
    try {
      const res = await api.listDrivers();
      setDrivers(unwrap(res));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (driversStream.data) {
      setDrivers(driversStream.data);
      setLoading(false);
    }
  }, [driversStream.data]);

  useEffect(() => {
    if (driversStream.error && !error) {
      setError(driversStream.error);
      setLoading(false);
    }
  }, [driversStream.error, error]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!isAdmin) {
      setError("Only admin users can create accounts.");
      return;
    }
    setPending(true);
    try {
      const created = await api.createDriver({
        role,
        employee_id: employeeId.trim().toUpperCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        pin,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        status,
      });
      setSuccess(`Created ${created.employee_id} (${inferRole(created.employee_id)})`);
      setEmployeeId("");
      setFirstName("");
      setLastName("");
      setPIN("");
      setEmail("");
      setPhone("");
      setShowCreate(false);
      await loadDrivers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setPending(false);
    }
  }

  function startEdit(driver: Driver) {
    setEditingId(driver.id);
    setEditRole(inferRole(driver.employee_id) as (typeof ROLE_OPTIONS)[number]["value"]);
    setEditEmployeeId(driver.employee_id);
    setEditFirstName(driver.first_name);
    setEditLastName(driver.last_name);
    setEditEmail(driver.email ?? "");
    setEditPhone(driver.phone ?? "");
    setEditStatus((driver.status || "active") as (typeof STATUS_OPTIONS)[number]);
    setResetPIN("");
  }

  function cancelEdit() {
    setEditingId(null);
    setResetPIN("");
  }

  async function onUpdateUser(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError(null);
    setSuccess(null);
    if (!isAdmin) {
      setError("Only admin users can update accounts.");
      return;
    }
    setEditPending(true);
    try {
      const updated = await api.updateDriver(editingId, {
        role: editRole,
        employee_id: editEmployeeId.trim().toUpperCase(),
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        email: editEmail.trim(),
        phone: editPhone.trim(),
        status: editStatus,
      });
      setSuccess(`Updated ${updated.employee_id} (${inferRole(updated.employee_id)})`);
      await loadDrivers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setEditPending(false);
    }
  }

  async function onResetPin() {
    if (!editingId) return;
    setError(null);
    setSuccess(null);
    if (!isAdmin) {
      setError("Only admin users can reset PINs.");
      return;
    }
    const pinValue = resetPIN.trim();
    if (pinValue.length < 4) {
      setError("PIN must be at least 4 characters.");
      return;
    }
    setResetPending(true);
    try {
      await api.resetDriverPin(editingId, pinValue);
      setSuccess(`Reset PIN for ${editEmployeeId.trim().toUpperCase()}`);
      setResetPIN("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset PIN");
    } finally {
      setResetPending(false);
    }
  }

  async function onDeleteUser(driver: Driver) {
    if (!isAdmin) {
      setError("Only admin users can delete accounts.");
      return;
    }
    if (!confirm(`Delete ${driver.employee_id}? This cannot be undone.`)) return;
    setError(null);
    setSuccess(null);
    setDeletePendingId(driver.id);
    try {
      await api.deleteDriver(driver.id);
      setSuccess(`Deleted ${driver.employee_id}`);
      if (editingId === driver.id) {
        cancelEdit();
      }
      await loadDrivers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setDeletePendingId(null);
    }
  }

  const roleMeta = ROLE_OPTIONS.find((r) => r.value === role);
  const editRoleMeta = ROLE_OPTIONS.find((r) => r.value === editRole);

  const consoleDrivers = drivers.filter((d) => inferRole(d.employee_id) !== "driver");
  const visibleDrivers = showAllAccounts ? drivers : consoleDrivers;
  const sortedDrivers = [...visibleDrivers].sort((a, b) => a.first_name.localeCompare(b.first_name));

  return (
    <div className="page users-page">
      <div className="users-header">
        <div>
          <h1>Users</h1>
          <p className="secondary-text">Console accounts and role-based access.</p>
        </div>
        <button
          className="btn primary users-add-btn"
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          disabled={!isAdmin}
          title={isAdmin ? undefined : "Only admin users can add accounts"}
        >
          {showCreate ? "Cancel" : "+ Add user"}
        </button>
      </div>

      {!isAdmin && (
        <div className="banner banner-error">
          Your role is <code>{session?.role ?? "unknown"}</code>. Only <code>admin</code> can
          manage users.
        </div>
      )}
      {success && <div className="banner banner-success">{success}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {showCreate && (
        <section className="card">
          <h3>Create user</h3>
          <form className="form-grid" onSubmit={onSubmit}>
            <div className="form-row">
              <label>
                Role
                <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Employee ID
                <input value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder={roleMeta?.prefix} required />
              </label>
              <label>
                PIN
                <input type="password" value={pin} onChange={(e) => setPIN(e.target.value)} minLength={4} required />
              </label>
            </div>
            <div className="form-row">
              <label>
                First name
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </label>
              <label>
                Last name
                <input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </label>
              <label>
                Status
                <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Email (optional)
                <input value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label>
                Phone (optional)
                <input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
            </div>
            <button className="btn primary" type="submit" disabled={!isAdmin || pending}>
              {pending ? "Creating…" : "Create user"}
            </button>
          </form>
        </section>
      )}

      {editingId && (
        <section className="card">
          <h3>Edit user</h3>
          <form className="form-grid" onSubmit={onUpdateUser}>
            <div className="form-row">
              <label>
                Role
                <select value={editRole} onChange={(e) => setEditRole(e.target.value as typeof editRole)}>
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Employee ID
                <input value={editEmployeeId} onChange={(e) => setEditEmployeeId(e.target.value)} placeholder={editRoleMeta?.prefix} required />
              </label>
              <label>
                Status
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value as typeof editStatus)}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                First name
                <input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} required />
              </label>
              <label>
                Last name
                <input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} required />
              </label>
            </div>
            <div className="form-row">
              <label>
                Email (optional)
                <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </label>
              <label>
                Phone (optional)
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Reset PIN
                <input type="password" value={resetPIN} onChange={(e) => setResetPIN(e.target.value)} minLength={4} />
              </label>
              <button className="btn ghost" type="button" onClick={() => void onResetPin()} disabled={!isAdmin || resetPending || resetPIN.trim().length < 4}>
                {resetPending ? "Resetting…" : "Reset PIN"}
              </button>
              <button className="btn ghost" type="button" onClick={cancelEdit} disabled={editPending}>Cancel</button>
              <button className="btn primary" type="submit" disabled={!isAdmin || editPending}>
                {editPending ? "Updating…" : "Update user"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card users-list-card">
        <div className="users-list-head">
          <h3>
            <span className="users-bar" />
            {showAllAccounts ? "All accounts" : "Console users"}
            <span className="users-count-pill">{visibleDrivers.length} accounts</span>
          </h3>
          <button type="button" className="users-toggle-all" onClick={() => setShowAllAccounts((v) => !v)}>
            {showAllAccounts ? "Show console users only" : `Show all accounts (${drivers.length})`}
          </button>
        </div>

        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th><th>Employee ID</th><th>Role</th><th>Status</th><th>Last login</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && drivers.length === 0 && (
                <tr><td colSpan={6} className="empty">Loading…</td></tr>
              )}
              {!loading && drivers.length === 0 && (
                <tr><td colSpan={6} className="empty">— none —</td></tr>
              )}
              {sortedDrivers.map((d) => {
                const roleValue = inferRole(d.employee_id);
                const active = (d.status || "active").toLowerCase() === "active";
                const isSelected = editingId === d.id;
                return (
                  <tr key={d.id} className={"users-row" + (isSelected ? " users-row-selected" : "")}>
                    <td>
                      <div className="users-name-cell">
                        <span className="users-avatar">{initials(d.first_name, d.last_name)}</span>
                        <div className="users-name-info">
                          <div className="users-name">{d.first_name} {d.last_name}</div>
                          {d.email && <div className="secondary-text">{d.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="mono">{d.employee_id}</td>
                    <td><span className={`users-role-pill ${roleTone(roleValue)}`}>{roleLabel(roleValue)}</span></td>
                    <td><span className={`users-status-pill ${active ? "ok" : "muted"}`}>{active ? "ACTIVE" : (d.status || "disabled").toUpperCase()}</span></td>
                    <td className="secondary-text">{fmtLastLogin(d.last_login_at)}</td>
                    <td>
                      {isAdmin ? (
                        <div className="users-row-actions">
                          <button className="btn ghost users-edit-btn" onClick={() => startEdit(d)} type="button">Edit</button>
                          <button
                            className="btn ghost users-delete-btn"
                            onClick={() => void onDeleteUser(d)}
                            type="button"
                            disabled={deletePendingId === d.id}
                          >
                            {deletePendingId === d.id ? "…" : "Delete"}
                          </button>
                        </div>
                      ) : (
                        <span className="secondary-text">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
